import { useEffect, useMemo, useState } from "react";
import {
  listarSesiones,
  listarPacientes,
  crearSesion,
  obtenerPaciente,
  listarEspecialidades,
} from "../api";
import "./AgendarSesion.css";

/**
 * Profesionales reales del centro.
 *
 * IDs:
 * 1  = Administración
 * 2  = Teresa
 * 3  = Mercedes
 * 7  = María
 * 17 = Marina
 */
const PROFESSIONALS = {
  psicologia: [
    { id: 2, name: "Teresa" },
    { id: 17, name: "Marina" },
  ],
  logopeda: [{ id: 3, name: "Mercedes" }],
  nutricion: [{ id: 7, name: "María" }],
  administracion: [{ id: 1, name: "Administración" }],
};

/**
 * Servicios disponibles por especialidad.
 */
const SERVICES_BY_SPEC = {
  psicologia: [
    {
      code: "consulta_inicial",
      label: "Consulta inicial (20 min)",
      duration: 20,
    },
    {
      code: "psicoterapia",
      label: "Psicoterapia (45 min)",
      duration: 45,
    },
    {
      code: "estimulacion",
      label: "Estimulación (45 min)",
      duration: 45,
    },
    {
      code: "terapia_familiar",
      label: "Terapia familiar (60 min)",
      duration: 60,
    },
    {
      code: "psicologia_perinatal",
      label: "Psicología perinatal (50 min)",
      duration: 50,
    },
    {
      code: "asesoramiento_crianza",
      label: "Asesoramiento crianza (50 min)",
      duration: 50,
    },
    {
      code: "atencion_temprana",
      label: "Atención temprana (45 min)",
      duration: 45,
    },
    {
      code: "reeducacion_psicopedagogica",
      label: "Reeducación psicopedagógica (45 min)",
      duration: 45,
    },
    {
      code: "entrevista_inicial",
      label: "Entrevista inicial (45 min)",
      duration: 45,
    },
    {
      code: "evaluacion_psicologica",
      label: "Evaluación psicológica (60 min)",
      duration: 60,
    },
    {
      code: "intervencion_psicoterapia_mes",
      label: "Intervención psicoterapia mensual (45 min)",
      duration: 45,
    },
    {
      code: "intervencion_estimulacion_mes",
      label: "Intervención estimulación mensual (45 min)",
      duration: 45,
    },
    {
      code: "reeducacion_pedagogica_NEAE_mes",
      label: "Reeducación pedagógica NEAE mes (45 min)",
      duration: 45,
    },
    {
      code: "altas_capacidades_NEAE_mes",
      label: "Altas capacidades NEAE mes (45 min)",
      duration: 45,
    },
  ],

  logopeda: [
    {
      code: "consulta_inicial",
      label: "Consulta inicial (20 min)",
      duration: 20,
    },
    {
      code: "entrevista_inicial",
      label: "Entrevista inicial (45 min)",
      duration: 45,
    },
    {
      code: "logopedia",
      label: "Logopedia (45 min)",
      duration: 45,
    },
    {
      code: "deglucion",
      label: "Deglución (30 min)",
      duration: 30,
    },
    {
      code: "evaluacion_logopedia",
      label: "Evaluación logopedia (60 min)",
      duration: 60,
    },
    {
      code: "intervencion_logopedia_mes",
      label: "Intervención logopedia mensual (45 min)",
      duration: 45,
    },
    {
      code: "reeducacion_lenguaje_NEAE_mes",
      label: "Reeducación del lenguaje NEAE mes (45 min)",
      duration: 45,
    },
  ],

  administracion: [
    {
      code: "gestion_administrativa",
      label: "Gestión administrativa (30 min)",
      duration: 30,
    },
    {
      code: "gestion_beca",
      label: "Gestión beca NEAE (30 min)",
      duration: 30,
    },
  ],

  nutricion: [
    {
      code: "consulta_inicial",
      label: "Consulta inicial (20 min)",
      duration: 20,
    },
    {
      code: "terapia_familiar",
      label: "Terapia familiar (60 min)",
      duration: 60,
    },
    {
      code: "consulta_nutricional",
      label: "Consulta nutricional (45 min)",
      duration: 45,
    },
  ],
};

/**
 * Psicología-m y psicología-g siguen existiendo en algunos
 * pacientes antiguos, pero ambas utilizan el catálogo de Psicología.
 */
function getBaseSpecialtyCode(code) {
  const value = String(code || "").toLowerCase().trim();

  if (
    value === "psicologia" ||
    value === "psicologia-m" ||
    value === "psicologia-g"
  ) {
    return "psicologia";
  }

  return value;
}

function normalize(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function extractPatientSpecialties(patient, allSpecs) {
  if (
    Array.isArray(patient?.specialties) &&
    patient.specialties.length
  ) {
    const arr = patient.specialties;

    if (typeof arr[0] === "object" && arr[0] !== null) {
      const hasFlag = [
        "selected",
        "checked",
        "asignada",
        "assigned",
        "is_selected",
        "isChecked",
      ].some((key) => key in (arr[0] || {}));

      const filtered = hasFlag
        ? arr.filter(
            (item) =>
              item.selected ||
              item.checked ||
              item.asignada ||
              item.assigned ||
              item.is_selected ||
              item.isChecked
          )
        : arr;

      return filtered
        .map((item) => ({
          code: String(item.code || "").trim(),
          nombre: String(
            item.nombre ||
              item.name ||
              item.label ||
              item.code ||
              ""
          ).trim(),
        }))
        .filter((item) => item.code);
    }

    return arr
      .map((code) => ({
        code: String(code || "").trim(),
        nombre: String(code || "").trim(),
      }))
      .filter((item) => item.code);
  }

  const candidates = [
    patient?.especialidades,
    patient?.especialidades_asignadas,
    patient?.specialty_codes,
    patient?.specialties_codes,
    patient?.specialtyCodes,
    patient?.patient_specialties,
  ].find((value) => Array.isArray(value) && value.length);

  if (Array.isArray(candidates) && candidates.length) {
    if (
      typeof candidates[0] === "object" &&
      candidates[0] !== null
    ) {
      return candidates
        .map((item) => {
          const obj = item.specialty || item;

          return {
            code: String(obj.code || "").trim(),
            nombre: String(
              obj.nombre ||
                obj.name ||
                obj.label ||
                obj.code ||
                ""
            ).trim(),
          };
        })
        .filter((item) => item.code);
    }

    return candidates
      .map((code) => ({
        code: String(code || "").trim(),
        nombre: String(code || "").trim(),
      }))
      .filter((item) => item.code);
  }

  if (
    Array.isArray(patient?.specialty_ids) &&
    patient.specialty_ids.length &&
    Array.isArray(allSpecs) &&
    allSpecs.length
  ) {
    const result = patient.specialty_ids
      .map((id) => {
        const specialty = allSpecs.find(
          (item) => Number(item.id) === Number(id)
        );

        if (!specialty) return null;

        return {
          code: String(specialty.code).trim(),
          nombre: String(
            specialty.nombre || specialty.code
          ).trim(),
        };
      })
      .filter(Boolean);

    if (result.length) return result;
  }

  const legacyServicios = Array.isArray(patient?.servicios)
    ? patient.servicios
    : String(patient?.servicios || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

  if (legacyServicios.length) {
    if (Array.isArray(allSpecs) && allSpecs.length) {
      const result = legacyServicios
        .map((code) => {
          const specialty = allSpecs.find(
            (item) => item.code === code
          );

          return specialty
            ? {
                code: specialty.code,
                nombre: specialty.nombre || specialty.code,
              }
            : {
                code,
                nombre: code,
              };
        })
        .filter((item) => item.code);

      if (result.length) return result;
    }

    return legacyServicios.map((code) => ({
      code,
      nombre: code,
    }));
  }

  if (patient?.specialty) {
    const code = String(patient.specialty).trim();

    return code
      ? [
          {
            code,
            nombre: code,
          },
        ]
      : [];
  }

  return [];
}

function formatLocalDateTime(date) {
  const pad = (number) =>
    number < 10 ? `0${number}` : String(number);

  return (
    `${date.getFullYear()}-` +
    `${pad(date.getMonth() + 1)}-` +
    `${pad(date.getDate())} ` +
    `${pad(date.getHours())}:` +
    `${pad(date.getMinutes())}:` +
    `${pad(date.getSeconds())}`
  );
}

export default function AgendarSesion({
  preselectedPatientId,
}) {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(
    preselectedPatientId || ""
  );

  const [patientSpec, setPatientSpec] = useState("");
  const [availableSpecs, setAvailableSpecs] = useState([]);
  const [allSpecs, setAllSpecs] = useState([]);

  const [professionalId, setProfessionalId] =
    useState("");

  const [service, setService] = useState("");
  const [date, setDate] = useState("");

  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] =
    useState(null);

  const [isGroup, setIsGroup] = useState(false);
  const [groupSize, setGroupSize] = useState(2);

  const [msg, setMsg] = useState("");
  const [q, setQ] = useState("");
  const [loadingSlots, setLoadingSlots] =
    useState(false);

  const baseSpecialty =
    getBaseSpecialtyCode(patientSpec);

  const professionals =
    PROFESSIONALS[baseSpecialty] || [];

  const availableServices =
    SERVICES_BY_SPEC[baseSpecialty] || [];

  const selectedService = availableServices.find(
    (item) => item.code === service
  );

  /**
   * Catálogo de especialidades.
   */
  useEffect(() => {
    (async () => {
      try {
        const response = await listarEspecialidades();

        const items = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
          ? response.items
          : [];

        setAllSpecs(items);
      } catch (error) {
        console.error(
          "Error cargando catálogo de especialidades",
          error
        );

        setAllSpecs([]);
      }
    })();
  }, []);

  /**
   * Cargar pacientes.
   */
  useEffect(() => {
    (async () => {
      try {
        const all = [];
        let page = 1;
        let pages = 1;

        do {
          const response = await listarPacientes({
            page,
            page_size: 200,
          });

          if (response?.items?.length) {
            all.push(...response.items);
          }

          pages = Number(response?.pages || 1);
          page += 1;
        } while (page <= pages);

        setPatients(all);
      } catch (error) {
        console.error(
          "Error cargando pacientes",
          error
        );
      }
    })();
  }, []);

  /**
   * Cargar especialidades del paciente.
   */
  useEffect(() => {
    (async () => {
      if (!patientId) {
        setAvailableSpecs([]);
        setPatientSpec("");
        setProfessionalId("");
        setService("");
        setSelectedSlot(null);
        setSlots([]);
        return;
      }

      try {
        const patient = await obtenerPaciente(
          patientId
        );

        const specialties =
          extractPatientSpecialties(
            patient,
            allSpecs
          );

        setAvailableSpecs(specialties);

        const firstCode =
          specialties?.[0]?.code || "";

        setPatientSpec(firstCode);
        setProfessionalId("");
        setService("");
        setSelectedSlot(null);
        setSlots([]);
      } catch (error) {
        console.error(
          "Error cargando paciente",
          error
        );

        setAvailableSpecs([]);
        setPatientSpec("");
        setProfessionalId("");
        setService("");
        setSelectedSlot(null);
        setSlots([]);
      }
    })();
  }, [patientId, allSpecs]);

  /**
   * Cuando cambia la especialidad:
   * - si solo existe una profesional, se asigna;
   * - si hay varias, debe elegirse.
   */
  useEffect(() => {
    setService("");
    setSelectedSlot(null);
    setSlots([]);
    setMsg("");

    if (professionals.length === 1) {
      setProfessionalId(
        String(professionals[0].id)
      );
    } else {
      setProfessionalId("");
    }
  }, [patientSpec]);

  /**
   * Generar huecos y comprobar ocupación de la profesional.
   */
  useEffect(() => {
    if (
      !date ||
      !selectedService ||
      !professionalId
    ) {
      setSlots([]);
      setSelectedSlot(null);
      return;
    }

    let cancelled = false;

    const loadSlots = async () => {
      try {
        setLoadingSlots(true);
        setSelectedSlot(null);

        const duration =
          selectedService.duration;

        /*
         * Se mantiene la rejilla flexible:
         * 15 minutos cuando la duración es múltiplo de 15;
         * 5 minutos para servicios de 20 o 50 minutos.
         */
        const stepMinutes =
          duration % 15 === 0 ? 15 : 5;

        const generateSlots = (
          startHHMM,
          endHHMM,
          step
        ) => {
          const result = [];

          let cursor = new Date(
            `${date}T${startHHMM}:00`
          );

          const blockEnd = new Date(
            `${date}T${endHHMM}:00`
          );

          const stepMs = step * 60000;
          const durationMs = duration * 60000;

          while (
            cursor.getTime() + durationMs <=
            blockEnd.getTime()
          ) {
            result.push({
              start: new Date(cursor),
              end: new Date(
                cursor.getTime() + durationMs
              ),
              occupied: false,
            });

            cursor = new Date(
              cursor.getTime() + stepMs
            );
          }

          return result;
        };

        /*
         * Agenda interna completa.
         * Posteriormente filtramos por professional_id.
         */
        let generatedSlots = generateSlots(
          "10:00",
          "21:00",
          stepMinutes
        );

        const response = await listarSesiones({
          date,
        });

        const allOccupied =
          response?.items || [];

        const professionalOccupied =
          allOccupied.filter(
            (session) =>
              Number(session.professional_id) ===
              Number(professionalId)
          );

        generatedSlots = generatedSlots.map(
          (slot) => {
            const clashes =
              professionalOccupied.filter(
                (session) =>
                  slot.start <
                    new Date(
                      session.end_datetime
                    ) &&
                  slot.end >
                    new Date(
                      session.start_datetime
                    )
              );

            /*
             * Cualquier sesión que se solape bloquea el hueco.
             * No permitimos seleccionar un horario reservado,
             * aunque la nueva sesión se haya marcado como múltiple.
             */
            const occupied = clashes.length > 0;

            return {
              ...slot,
              occupied,
            };
          }
        );

        if (!cancelled) {
          setSlots(generatedSlots);
        }
      } catch (error) {
        console.error(
          "Error calculando huecos",
          error
        );

        if (!cancelled) {
          setSlots([]);
          setMsg(
            "No se pudieron cargar los huecos"
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingSlots(false);
        }
      }
    };

    loadSlots();

    return () => {
      cancelled = true;
    };
  }, [
    date,
    selectedService,
    professionalId,
    isGroup,
  ]);

  const submit = async () => {
    try {
      setMsg("");

      if (
        !patientId ||
        !patientSpec ||
        !professionalId ||
        !service ||
        !selectedSlot
      ) {
        setMsg(
          "Selecciona paciente, especialidad, profesional, servicio y horario"
        );

        return;
      }

      /*
       * Volvemos a comprobar el horario justo antes
       * de guardar, para evitar dobles reservas.
       */
      const response = await listarSesiones({
        date,
      });

      const occupied = (
        response?.items || []
      ).filter(
        (session) =>
          Number(session.professional_id) ===
          Number(professionalId)
      );

      const clashes = occupied.filter(
        (session) =>
          selectedSlot.start <
            new Date(session.end_datetime) &&
          selectedSlot.end >
            new Date(session.start_datetime)
      );

      const hasIndividual = clashes.some(
        (session) => !session.is_group
      );

      if (hasIndividual && !isGroup) {
        setMsg(
          "Ese horario ya está ocupado para la profesional seleccionada ❌"
        );

        return;
      }

      if (!selectedService) {
        setMsg(
          "Servicio inválido para esa especialidad"
        );

        return;
      }

      await crearSesion({
        patient_id: Number(patientId),

        /*
         * Conservamos la especialidad asignada al paciente.
         * Puede seguir siendo psicologia-m o psicologia-g
         * en pacientes históricos.
         */
        specialty: patientSpec,

        professional_id:
          Number(professionalId),

        service_code: service,

        start_datetime:
          formatLocalDateTime(
            selectedSlot.start
          ),

        end_datetime:
          formatLocalDateTime(
            selectedSlot.end
          ),

        duration_minutes:
          selectedService.duration,

        is_group: isGroup ? 1 : 0,

        group_size: isGroup
          ? Number(groupSize)
          : null,
      });

      setMsg("Sesión agendada ✅");
      setSelectedSlot(null);

      /*
       * Recargar huecos mediante un cambio mínimo
       * de fecha para reflejar inmediatamente la sesión.
       */
      /*
       * Marcamos como ocupados todos los huecos de la rejilla
       * que se solapan con la sesión recién creada.
       *
       * Ejemplo: una sesión 16:30-17:15 también debe bloquear
       * los comienzos 16:45 y 17:00 si se muestran en la rejilla.
       */
      setSlots((current) =>
        current.map((slot) => {
          const overlapsCreatedSession =
            slot.start < selectedSlot.end &&
            slot.end > selectedSlot.start;

          return overlapsCreatedSession
            ? {
                ...slot,
                occupied: true,
              }
            : slot;
        })
      );
    } catch (error) {
      setMsg(
        `Error: ${
          error.message ||
          "Error desconocido"
        }`
      );
    }
  };

  const filteredPatients = useMemo(() => {
    const normalizedQuery = normalize(q);

    if (!normalizedQuery) {
      return patients;
    }

    const tokens = normalizedQuery
      .split(" ")
      .filter(Boolean);

    return patients.filter((patient) => {
      const normalizedName = normalize(
        patient.nombre
      );

      return tokens.every((token) =>
        normalizedName.includes(token)
      );
    });
  }, [patients, q]);

  return (
    <div className="agenda-sesion">
      <h2>Agendar sesión</h2>

      <div className="form-row">
        <label>Paciente</label>

        <input
          type="text"
          value={q}
          onChange={(event) =>
            setQ(event.target.value)
          }
          placeholder="Buscar paciente..."
        />

        <select
          value={patientId}
          onChange={(event) =>
            setPatientId(event.target.value)
          }
        >
          <option value="">
            -- Seleccionar --
          </option>

          {filteredPatients.map((patient) => (
            <option
              key={patient.id}
              value={patient.id}
            >
              {patient.nombre}
            </option>
          ))}
        </select>
      </div>

      {patientId && (
        <>
          <div className="form-row">
            <label>Especialidad</label>

            {availableSpecs.length > 1 ? (
              <select
                value={patientSpec}
                onChange={(event) =>
                  setPatientSpec(
                    event.target.value
                  )
                }
              >
                {availableSpecs.map(
                  (specialty) => (
                    <option
                      key={specialty.code}
                      value={specialty.code}
                    >
                      {specialty.nombre ||
                        specialty.code}
                    </option>
                  )
                )}
              </select>
            ) : availableSpecs.length ===
              1 ? (
              <input
                type="text"
                value={
                  availableSpecs[0].nombre ||
                  availableSpecs[0].code
                }
                disabled
              />
            ) : (
              <p style={{ margin: 0 }}>
                Este paciente no tiene
                especialidades asignadas ❌
              </p>
            )}
          </div>

          {patientSpec && (
            <>
              <div className="form-row">
                <label>Profesional</label>

                {professionals.length > 1 ? (
                  <select
                    value={professionalId}
                    onChange={(event) => {
                      setProfessionalId(
                        event.target.value
                      );

                      setSelectedSlot(null);
                    }}
                  >
                    <option value="">
                      -- Seleccionar profesional --
                    </option>

                    {professionals.map(
                      (professional) => (
                        <option
                          key={professional.id}
                          value={professional.id}
                        >
                          {professional.name}
                        </option>
                      )
                    )}
                  </select>
                ) : professionals.length ===
                  1 ? (
                  <input
                    type="text"
                    value={
                      professionals[0].name
                    }
                    disabled
                  />
                ) : (
                  <p style={{ margin: 0 }}>
                    No hay profesionales
                    configuradas para esta
                    especialidad.
                  </p>
                )}
              </div>

              <div className="form-row">
                <label>Servicio</label>

                <select
                  value={service}
                  onChange={(event) => {
                    setService(
                      event.target.value
                    );

                    setSelectedSlot(null);
                  }}
                >
                  <option value="">
                    -- Seleccionar --
                  </option>

                  {availableServices.map(
                    (item) => (
                      <option
                        key={item.code}
                        value={item.code}
                      >
                        {item.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="form-row">
                <label>Fecha</label>

                <input
                  type="date"
                  value={date}
                  onChange={(event) => {
                    setDate(
                      event.target.value
                    );

                    setSelectedSlot(null);
                  }}
                />
              </div>

              <div className="slots-panel">
                {loadingSlots && (
                  <p>Cargando huecos…</p>
                )}

                {!loadingSlots &&
                  slots.length === 0 && (
                    <p>
                      Selecciona profesional,
                      fecha y servicio.
                    </p>
                  )}

                {!loadingSlots &&
                  slots.map((slot, index) => (
                    <button
                      key={`${slot.start.toISOString()}-${index}`}
                      type="button"
                      className={[
                        "slot-bubble",
                        slot.occupied
                          ? "occupied"
                          : "",
                        selectedSlot === slot
                          ? "selected"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={slot.occupied}
                      aria-disabled={slot.occupied}
                      onClick={() => {
                        if (slot.occupied) {
                          return;
                        }

                        setSelectedSlot(slot);
                      }}
                    >
                      {slot.start.toLocaleTimeString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                      {" - "}
                      {slot.end.toLocaleTimeString(
                        [],
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </button>
                  ))}
              </div>

              <div className="form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={isGroup}
                    onChange={(event) =>
                      setIsGroup(
                        event.target.checked
                      )
                    }
                  />

                  Sesión múltiple
                </label>

                {isGroup && (
                  <input
                    type="number"
                    min="2"
                    max="5"
                    value={groupSize}
                    onChange={(event) =>
                      setGroupSize(
                        event.target.value
                      )
                    }
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={
          !patientId ||
          !patientSpec ||
          !professionalId ||
          !service ||
          !selectedSlot
        }
      >
        Agendar
      </button>

      {msg && <p>{msg}</p>}
    </div>
  );
}